import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { db } from "@/firebase";
import { doc, setDoc, increment } from "firebase/firestore";
import React, { Suspense, lazy } from "react";
import { createPortal } from "react-dom";
import "./i18n/config";
import { ScrollToTop } from "./components/ScrollToTop";
import { Loading } from "./components/ui/loading";
import { CacheProvider } from "@/context/CacheContext";
import { Navbar } from "@/components/Navbar";
import { Topbar } from "@/components/Topbar";

// Lazy load pages
const Index = lazy(() => import("./pages/Index"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const Cart = lazy(() => import("./pages/Cart"));
const Admin = lazy(() => import("./pages/Admin"));
const About = lazy(() => import("./pages/About"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Sellers = lazy(() => import("./pages/Locations"));
const Careers = lazy(() => import("./pages/Careers"));
const Delivery = lazy(() => import("./pages/Delivery"));
const AddProduct = lazy(() => import("./pages/AddProduct"));
const RegisterSupplier = lazy(() => import("./pages/RegisterSupplier"));
const ProductManagementPage = lazy(() => import("./pages/ProductManagementPage"));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

function LoadingFallback() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-lg">جاري التحميل...</p>
      </div>
    </div>
  );
}

const App = () => {
  const location = useLocation();

  // Track page views per route
  React.useEffect(() => {
    const path = location.pathname || "/";
    const search = location.search || "";
    const fullPath = path + search;
    // Use encoded path as doc id
    const docId = encodeURIComponent(fullPath);
    (async () => {
      try {
        await setDoc(
          doc(db, "pageViews", docId),
          {
            count: increment(1),
            path: fullPath,
            lastVisitedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      } catch {
        // ignore analytics errors
      }
    })();
  }, [location.pathname, location.search]);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <CacheProvider>
          <TooltipProvider>
            {createPortal(<Toaster />, document.body)}
            <ScrollToTop />

            <Routes>
              <Route path="/" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Index />
                </Suspense>
              } />
              <Route path="/products" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Products />
                </Suspense>
              } />
              <Route path="/products/:id" element={
                <Suspense fallback={<LoadingFallback />}>
                  <ProductDetails />
                </Suspense>
              } />
              <Route path="/cart" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Cart />
                </Suspense>
              } />
              <Route path="/admin" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Admin />
                </Suspense>
              } />
              <Route path="/admin/analytics" element={
                <Suspense fallback={<LoadingFallback />}>
                  {React.createElement((lazy(() => import('./pages/AdminAnalytics')) as any))}
                </Suspense>
              } />
              <Route path="/about" element={
                <Suspense fallback={<LoadingFallback />}>
                  <About />
                </Suspense>
              } />
              <Route path="/faq" element={
                <Suspense fallback={<LoadingFallback />}>
                  <FAQ />
                </Suspense>
              } />
              <Route path="/sellers" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Sellers />
                </Suspense>
              } />
              <Route path="/careers" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Careers />
                </Suspense>
              } />
              <Route path="/delivery" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Delivery />
                </Suspense>
              } />
              <Route path="/add-product" element={
                <Suspense fallback={<LoadingFallback />}>
                  <AddProduct />
                </Suspense>
              } />
              <Route path="/register-supplier" element={
                <Suspense fallback={<LoadingFallback />}>
                  <RegisterSupplier />
                </Suspense>
              } />
              <Route path="/register-supplier/hema" element={
                <Suspense fallback={<LoadingFallback />}>
                  <RegisterSupplier />
                </Suspense>
              } />
              <Route path="/register-supplier/moaw" element={
                <Suspense fallback={<LoadingFallback />}>
                  <RegisterSupplier />
                </Suspense>
              } />
              <Route path="/product-management" element={
                <Suspense fallback={<LoadingFallback />}>
                  <ProductManagementPage />
                </Suspense>
              } />
            </Routes>
          </TooltipProvider>
        </CacheProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
